import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUserPlus, FiSettings, FiTrendingUp } from 'react-icons/fi';
import { Icon } from '../ui/Icon';
import { theme } from '../../styles/theme';

const HowItWorksSection = styled.section`
  padding: ${theme.spacing['5xl']} 0;
  background: ${theme.colors.white};
  scroll-margin-top: 80px; /* Account for fixed navbar */
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const SectionTitle = styled(motion.h2)`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${theme.typography.sizes.xl};
  color: ${theme.colors.gray[600]};
  max-width: 600px;
  margin: 0 auto;
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing['3xl']};
  margin-top: ${theme.spacing['4xl']};
  
  @media (max-width: ${theme.breakpoints.md}) {
    gap: ${theme.spacing['2xl']};
  }
`;

const StepCard = styled(motion.div)`
  text-align: center;
  position: relative;
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  background: ${theme.colors.gradients.hero};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.xl} auto;
  color: ${theme.colors.white};
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  position: relative;
  z-index: 2;
`;

const StepIcon = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, ${theme.colors.secondary}, ${theme.colors.accent});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.xl} auto;
  
  svg {
    width: 50px;
    height: 50px;
    color: ${theme.colors.white};
  }
`;

const StepTitle = styled.h3`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.sizes.xl};
`;

const StepDescription = styled.p`
  color: ${theme.colors.gray[600]};
  line-height: ${theme.typography.lineHeights.relaxed};
  max-width: 280px;
  margin: 0 auto;
`;

const ConnectorLine = styled.div`
  position: absolute;
  top: 50px;
  left: 50%;
  width: calc(100% - 100px);
  height: 2px;
  background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.accent});
  transform: translateX(-50%);
  z-index: 0;
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;



const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

const stepVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 }
};

export const HowItWorks: React.FC = () => {
  return (
    <HowItWorksSection id="how-it-works">
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            How It Works
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Get started with Care++ in three simple steps and transform 
            your care management process.
          </SectionSubtitle>
        </SectionHeader>
        
        <div style={{ position: 'relative' }}>
          <ConnectorLine />
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <StepsContainer>
              <motion.div variants={stepVariants}>
                <StepCard>
                  <StepIcon>
                    <Icon icon={FiUserPlus} />
                  </StepIcon>
                  <StepNumber>1</StepNumber>
                  <StepTitle>Quick Setup</StepTitle>
                  <StepDescription>Register your organisation and set up patient and carer profiles in minutes through our intuitive interface.</StepDescription>
                </StepCard>
              </motion.div>
              
              <motion.div variants={stepVariants}>
                <StepCard>
                  <StepIcon>
                    <Icon icon={FiSettings} />
                  </StepIcon>
                  <StepNumber>2</StepNumber>
                  <StepTitle>Configure Care Plans</StepTitle>
                  <StepDescription>Customize care routines, schedules, and carer tasks. Set up automated reminders and medical preferences.</StepDescription>
                </StepCard>
              </motion.div>
              
              <motion.div variants={stepVariants}>
                <StepCard>
                  <StepIcon>
                    <Icon icon={FiTrendingUp} />
                  </StepIcon>
                  <StepNumber>3</StepNumber>
                  <StepTitle>Track & Optimise</StepTitle>
                  <StepDescription>Monitor patient status, view care analytics, and continuously improve outcomes with data-driven insights.</StepDescription>
                </StepCard>
              </motion.div>
            </StepsContainer>
          </motion.div>
        </div>
      </Container>
    </HowItWorksSection>
  );
};